@if (isLoading || isServicesInProgress) {
  <section
    class="flex min-h-screen flex-col border-l border-gray-200 bg-white"
    role="status"
    aria-label="Loading service detail"
    aria-busy="true"
  >
    <div class="w-full flex-1 animate-pulse px-6 py-4">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0 space-y-2">
          <div class="h-5 w-32 rounded bg-gray-200"></div>
          <div class="bg-gray-150 h-4 w-40 rounded"></div>
        </div>
        <div class="h-6 w-20 shrink-0 rounded-full bg-gray-200"></div>
      </div>

      <div class="border-gray-150 mt-6 border-b pb-2">
        <div class="h-4 w-28 rounded bg-gray-200"></div>
      </div>

      <div class="mt-3 grid grid-cols-3 gap-y-4">
        @for (item of [1, 2, 3, 4, 5, 6]; track item) {
          <div class="space-y-2">
            <div class="bg-gray-150 h-3 w-16 rounded"></div>
            <div class="h-4 w-20 rounded bg-gray-200"></div>
          </div>
        }
      </div>

      <div class="mt-8 space-y-3">
        <div class="h-4 w-24 rounded bg-gray-200"></div>
        <div class="bg-gray-150 h-16 w-full rounded"></div>
      </div>
    </div>

    <span class="sr-only">Loading service detail…</span>
  </section>
} @else if (selectedService) {
  <section
    class="flex min-h-screen flex-col md:border-l md:border-gray-200 md:bg-white"
    aria-labelledby="service-detail-heading"
  >
    <!-- Back link (mobile) -->
    <div class="flex w-full items-center bg-white text-base md:hidden">
      <a
        class="font-lato flex min-h-12 cursor-pointer items-center p-4 text-xs font-bold text-blue-700 uppercase"
        [routerLink]="['/services']"
      >
        <mat-icon class="mr-2 text-sm" aria-hidden="true">arrow_back</mat-icon>
        Back to Services
      </a>
    </div>

    <div class="w-full flex-1 px-6 py-4">
      <!-- Header: title + status -->
      <div
        class="flex flex-col-reverse items-start justify-between gap-4 md:flex-row"
      >
        <div class="min-w-0">
          <h2 id="service-detail-heading" class="uppercase">
            {{ selectedService.Name }}
          </h2>

          <!-- Location -->
          <div
            class="mt-1.5 flex items-center gap-1.5 max-md:flex-wrap max-md:items-start"
          >
            <span class="text-base capitalize">
              {{
                selectedService.CustomerLocationAlias ||
                  selectedService.Z_Location__r.Name
              }}
            </span>
            @if (
              selectedService.Status__c === 'In Service' &&
              selectedService.BanLookupId
            ) {
              <button
                type="button"
                (click)="onEditLocationName(selectedService)"
                class="font-lato ml-4 inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-blue-600 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-100 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 md:mt-2"
                [attr.aria-label]="
                  selectedService.CustomerLocationAlias
                    ? 'Edit location name'
                    : 'Add location name'
                "
              >
                <mat-icon
                  class="flex! h-3.5! w-3.5! items-center! justify-center! text-sm!"
                  aria-hidden="true"
                  >{{
                    selectedService.CustomerLocationAlias ? 'edit' : 'add'
                  }}</mat-icon
                >
                {{
                  selectedService.CustomerLocationAlias
                    ? 'Edit Location Name'
                    : 'Add Location Name'
                }}
              </button>
            }
          </div>
          @if (selectedService.CustomerLocationAlias) {
            <div class="text-brand-muted mt-1 text-sm tracking-wide uppercase">
              {{ selectedService.Z_Location__r.Name | uppercase }}
            </div>
          }
        </div>

        <span
          class="comcast-status-pill shrink-0"
          [ngClass]="{
            'comcast-status-pill--success':
              selectedService.Status__c === 'In Service',
            'comcast-status-pill--warning':
              selectedService.Status__c === 'New' ||
              selectedService.Status__c === 'Pending',
            'comcast-status-pill--danger':
              selectedService.Status__c === 'Canceled' ||
              selectedService.Status__c === 'Disconnected',
          }"
        >
          {{
            selectedService.Status__c === 'New'
              ? 'Pending'
              : selectedService.Status__c
          }}
        </span>
      </div>

      <!-- Service configuration -->
      <div
        class="border-gray-150 mt-8 flex items-center gap-1.5 border-b pb-2 font-semibold tracking-widest uppercase"
      >
        <div
          class="custom-icon circuit_dark w-2.5! shrink-0"
          aria-hidden="true"
        >
          service type icon
        </div>
        {{ selectedService.RecordType.Name }}
      </div>

      <!-- Meta grid -->
      <dl
        class="mt-3 grid grid-cols-3 gap-y-4 max-md:grid-cols-2 max-md:gap-x-4"
      >
        <!-- Bandwidth (with utilization link) -->
        @if (selectedService.Bandwidth_Downstream__c) {
          <div class="max-md:col-span-2">
            <dt
              class="text-brand-muted text-xs font-semibold tracking-widest uppercase"
            >
              Bandwidth
            </dt>
            <dd class="mt-1 flex items-center gap-1.5">
              <span
                class="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-sm font-medium whitespace-nowrap text-blue-600"
              >
                {{ selectedService.Bandwidth_Downstream__c }}
                @if (selectedService.Asymmetrical_Bandwidth__c) {
                  / {{ selectedService.Bandwidth_Upstream__c }}
                }
              </span>
            </dd>
            @if (selectedService.Status__c === 'In Service') {
              <dd>
                <a
                  routerLink="/services/p95-utilization"
                  [queryParams]="{
                    location: selectedService.Z_Location__r.Street_1__c,
                  }"
                  *ngxPermissionsOnly="Permission.NITEL_IQ_PARTICIPANT"
                  class="mt-0.5 inline-block text-xs hover:underline"
                >
                  View 95th percentile report
                </a>
              </dd>
            }
          </div>
        }

        <!-- Vendor -->
        @if (selectedService.Vendor__r) {
          <div>
            <dt
              class="text-brand-muted text-xs font-semibold tracking-widest uppercase"
            >
              Vendor
            </dt>
            <dd class="mt-1 text-sm font-medium">
              {{
                selectedService.Vendor__r.Display_Alias__c ||
                  selectedService.Vendor__r.Name
              }}
            </dd>
          </div>
        }

        <!-- Connector -->
        @if (selectedService.Connector_Type_Z_Location__c) {
          <div>
            <dt class="text-brand-muted text-xs tracking-widest uppercase">
              Connector
            </dt>
            <dd class="mt-1 text-sm font-medium">
              {{ selectedService.Connector_Type_Z_Location__c }}
            </dd>
          </div>
        }

        <!-- Term -->
        @if (selectedService.Term__c) {
          <div>
            <dt
              class="text-brand-muted text-xs font-semibold tracking-widest uppercase"
            >
              Term
            </dt>
            <dd class="mt-1 text-sm font-medium">
              {{ selectedService.Term__c }} months
            </dd>
          </div>
        }

        <!-- Start -->
        <div>
          <dt
            class="text-brand-muted text-xs font-semibold tracking-widest uppercase"
          >
            Start
          </dt>
          <dd class="mt-1 text-sm font-medium">
            {{ selectedService.Start_Of_Service__c | date: 'MMM d, yyyy' }}
          </dd>
        </div>

        <!-- End -->
        <div>
          <dt
            class="text-brand-muted text-xs font-semibold tracking-widest uppercase"
          >
            End
          </dt>
          <dd class="mt-1 text-sm font-medium">
            {{ selectedService.Current_Term_End_Date__c | date: 'MMM d, yyyy' }}
          </dd>
        </div>
      </dl>

      <!-- Billing components -->
      <app-service-billing
        [components]="
          $safeNavigationMigration(selectedService.Components__r?.records)
        "
        [monthlyTotal]="selectedService.MRR__c"
      ></app-service-billing>

      @if (selectedService.Status__c === 'In Service') {
        <div class="mt-10 flex w-full flex-col items-center gap-1.5">
          <button
            *ngxPermissionsOnly="Permission.TICKETING_WRITE"
            (click)="onCreateTicket()"
            class="btn-secondary w-full max-w-xs justify-center"
          >
            Create ticket
          </button>

          <p class="text-brand-muted text-sm">
            Report an issue with this service
          </p>
        </div>
      }
    </div>
  </section>
} @else {
  <section
    class="flex min-h-screen flex-col items-center justify-center border-l border-gray-200 bg-white px-6 py-20 text-center"
    role="status"
    aria-live="polite"
  >
    <div
      aria-hidden="true"
      class="custom-icon circuit_dark text-brand-muted mb-4 w-6!"
    >
      service type icon
    </div>
    <h2 class="font-montserrat text-lg font-semibold">No service selected</h2>
    <p class="font-lato text-brand-muted mt-1 max-w-55 text-sm leading-relaxed">
      Select a service from the list to view its details.
    </p>
  </section>
}
